// src/modules/app-views/omnichannel/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState, useCallback } from 'react';
import { Avatar, Button, Card, Flex, Grid, Drawer, Select, Splitter, Tooltip, theme } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { BiExpandAlt, BiCollapseAlt } from 'react-icons/bi';
import { useParams, useLocation } from 'react-router-dom';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import { getStorageModuleItem, setStorageModuleItem } from 'stores/module';
import { parsePhoneToDisplay } from 'utils/helpers';
import ChatMenu from './components/ChatMenu';
import Conversation from './components/Conversation';
import CustomerIntelligencePanel from './components/CustomerIntelligencePanel';
import TranslateLanguageSelect from './components/TranslateLanguageSelect';
import WhatsAppSourceTag from './components/WhatsAppSourceTag';
import OmnichannelDashboard from './dashboard';
import omnichannelService from '../../service';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_SIDEBAR_WIDTH = 450;
const MIN_SIDEBAR_WIDTH = '280px';
const MAX_SIDEBAR_WIDTH = '50%';
const STORAGE_KEY_SIDEBAR = 'omnichannelSidebarWidth';
const DEFAULT_INTELLIGENCE_PANEL_WIDTH = 380;
const MIN_INTELLIGENCE_PANEL_WIDTH = 200;
const MAX_INTELLIGENCE_PANEL_WIDTH = '40%';
const STORAGE_KEY_INTELLIGENCE_PANEL = 'omnichannelIntelligencePanelWidth';
const STORAGE_KEY_INTELLIGENCE_VISIBLE = 'omnichannelIntelligencePanelVisible';
const STORAGE_KEY_SELECTED_SOURCE = 'omnichannel_selected_source';
const SHOW_INTELLIGENCE_PANEL = false; // set to true when CustomerIntelligencePanel is ready

// Layout heights (px) — must match LESS variables in _template.less
const TOPBAR_HEIGHT = 70;
const TOPNAV_HEIGHT = 55;
const PAGE_HEADER_HEIGHT = 70;
const FOOTER_HEIGHT = 76;
const GAP_HEIGHT = 76;
// On mobile the page header and part of the gap are not rendered
const MOBILE_OFFSET_HEIGHT = 192;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const OmnichannelInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();
  const params = useParams();
  const location = useLocation();
  const setReadyStore = useZustand('module', 'setReady');
  const setLoadingPageStore = useZustand('boot', 'setLoadingPage');
  const storedCurrentView = useZustand('module', 'currentView');
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.lg;
  const hasConversation = !!params._id;
  // Derive module base path dynamically so it works regardless of which menu group hosts the module
  const modulePath = params._id
    ? location.pathname.slice(0, location.pathname.length - params._id.length - 1)
    : location.pathname.replace(/\/$/, '');
  const storedSidebarWidth = getStorageModuleItem(STORAGE_KEY_SIDEBAR);
  const storedIntelligencePanelWidth = getStorageModuleItem(STORAGE_KEY_INTELLIGENCE_PANEL);
  // null = never stored before → default visible; stored value is boolean
  const storedIntelligenceVisible = getStorageModuleItem(STORAGE_KEY_INTELLIGENCE_VISIBLE);
  const storedNavType = useZustand('theme', 'navType');
  const isDesktop = appService.isDesktop();
  const hasTopNav = storedNavType === 'top' && isDesktop;
  const offsetHeight = isMobile
    ? MOBILE_OFFSET_HEIGHT
    : GAP_HEIGHT +
      TOPBAR_HEIGHT +
      PAGE_HEADER_HEIGHT +
      FOOTER_HEIGHT +
      (hasTopNav ? TOPNAV_HEIGHT : 0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [conversationCount, setConversationCount] = useState(null); // null = loading
  const [sources, setSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [sourceSelectOpen, setSourceSelectOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // null means never stored → show by default; false = user hid it explicitly
  const [intelligenceVisible, setIntelligenceVisible] = useState(storedIntelligenceVisible !== false);
  // Translate language for the empty state selector (same localStorage key as Conversation)
  const [translateLang, setTranslateLang] = useState(
    () => localStorage.getItem('omnichannel_translate_lang') || 'none'
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Show SimpleLoadingPage on module entry. With a single route (/:_id? optional param),
  // the component never remounts on conversation clicks — only on initial module load.
  useEffect(() => {
    setLoadingPageStore({ visible: true });
  }, []);

  // Dismiss loading overlay once conversation list is loaded (same pattern as CRM Boards).
  // conversationCount starts as null; ChatMenu sets it to a number via onConversationsLoaded.
  useEffect(() => {
    if (conversationCount !== null) setReadyStore(true);
  }, [conversationCount]);

  // Load sources so the empty state can show the source selector.
  // Re-fetches when subscription changes so the selector shows the correct numbers.
  useEffect(() => {
    let active = true;

    const fetchSources = async () => {
      try {
        const [data] = await omnichannelService.getSources();
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setSources(list);
        // Initialize selected source from localStorage or first in list
        const stored = localStorage.getItem(STORAGE_KEY_SELECTED_SOURCE);
        if (stored && list.some(s => s._id === stored)) {
          setSelectedSourceId(stored);
        } else if (list.length > 0) {
          setSelectedSourceId(list[0]._id);
        } else {
          setSelectedSourceId(null);
        }
      } catch {
        // Sources unavailable — clear selector
        if (active) {
          setSources([]);
          setSelectedSourceId(null);
        }
      }
    };

    fetchSources();

    const handleRefresh = () => fetchSources();
    window.addEventListener('omnichannel:sources-refresh', handleRefresh);

    return () => {
      active = false;
      window.removeEventListener('omnichannel:sources-refresh', handleRefresh);
    };
  }, [storedSubscription?._id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleMainSplitterResize = useCallback(sizes => {
    // sizes is an array of pixel values — [sidebar, conversation] or [sidebar, conversation, intelligence]
    if (sizes?.[0]) {
      setStorageModuleItem(STORAGE_KEY_SIDEBAR, Math.round(sizes[0]));
    }
    if (sizes?.[2] > 0) {
      setStorageModuleItem(STORAGE_KEY_INTELLIGENCE_PANEL, Math.round(sizes[2]));
    }
  }, []);

  // Fired by the built-in AntD collapsible arrow on the divider
  // collapsed is boolean[] for each panel; index 2 = intelligence panel
  const handleSplitterCollapse = useCallback((collapsed) => {
    const isCollapsed = !!collapsed[2];
    setIntelligenceVisible(!isCollapsed);
    setStorageModuleItem(STORAGE_KEY_INTELLIGENCE_VISIBLE, !isCollapsed);
  }, []);

  // Source selector handlers for the empty state
  const handleEmptySourceChange = useCallback(sourceId => {
    setSelectedSourceId(sourceId);
    localStorage.setItem(STORAGE_KEY_SELECTED_SOURCE, sourceId);
  }, []);

  const handleEmptySourceSelect = useCallback(() => {
    setSourceSelectOpen(false);
  }, []);

  const handleEmptySourceOpenChange = useCallback(visible => {
    setSourceSelectOpen(visible);
  }, []);

  // Persist translate language from the empty state selector (same key as Conversation)
  const handleTranslateLangChange = useCallback(lang => {
    setTranslateLang(lang);
    localStorage.setItem('omnichannel_translate_lang', lang);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    // Show message after state update (read current value before toggle)
    appService.message({
      flag: 'i',
      text: !isFullscreen ? 'fullscreen_enabled' : 'fullscreen_disabled',
    });
  }, [isFullscreen]);

  // Shared conversation area content (used in both mobile and desktop)
  const conversationContent = hasConversation ? (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        height: '100%',
        borderBottomRightRadius: 10,
      }}
    >
      <Conversation isFullscreen={isFullscreen} onToggleFullscreen={handleToggleFullscreen} basePath={modulePath} />
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar: same controls as ConversationHeader (without contact info) */}
      <Flex
        align="center"
        justify="flex-end"
        gap={4}
        style={{ padding: '12px 12px 0', flexShrink: 0 }}
      >
        <TranslateLanguageSelect
          value={translateLang}
          onChange={handleTranslateLangChange}
          style={{ height: 40 }}
        />
        {sources.length > 0 && (
          <Select
            value={selectedSourceId}
            onChange={handleEmptySourceChange}
            open={sourceSelectOpen}
            onSelect={handleEmptySourceSelect}
            onOpenChange={handleEmptySourceOpenChange}
            style={{ height: 40, minWidth: 220 }}
            placeholder="Selecionar fonte"
            popupMatchSelectWidth={false}
            options={sources.map(s => ({
              value: s._id,
              label: (
                <Flex align="center" gap={6} className="pr-2">
                  <Avatar src={s.profilePicture || undefined} size={24} style={{ flexShrink: 0 }}>
                    {(s.displayName || 'W').charAt(0).toUpperCase()}
                  </Avatar>
                  <Flex vertical style={{ lineHeight: 1.3 }}>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{s.displayName || 'WhatsApp'}</span>
                    <span style={{ fontSize: 10, color: '#999' }}>{formatSourcePhone(s.phoneNumber) || '—'}</span>
                  </Flex>
                  <WhatsAppSourceTag sourceType={s.sourceType} size="small" style={{ marginLeft: 'auto' }} />
                </Flex>
              ),
            }))}
          />
        )}
        <Tooltip title={isFullscreen ? 'Restaurar janela' : 'Exibir tela toda'}>
          <Button
            icon={isFullscreen
              ? <BiCollapseAlt style={{ fontSize: 18, display: 'flex' }} />
              : <BiExpandAlt style={{ fontSize: 18, display: 'flex' }} />
            }
            onClick={handleToggleFullscreen}
          />
        </Tooltip>
      </Flex>

      {/* Centered empty state image */}
      <div className="chat-content-empty" style={{ flex: 1, flexDirection: 'column' }}>
        <img src="/img/others/img-11.png" alt="Start a Conversation" />
        <h1 className="font-weight-light">
          {translateX(conversationCount > 0 ? 'select_a_conversation' : 'no_conversation')}
        </h1>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Dashboard view
  if (storedCurrentView === 'dashboard') {
    return <OmnichannelDashboard />;
  }

  // Main (init) view
  return (
    <>
      {/* When fullscreen, override stacking contexts of parent elements so chat floats above TopBar */}
      {isFullscreen && (
        <style>{`
        .ant-layout-content { z-index: 1000 !important; }
        #server-wrapper-content { z-index: 1000 !important; }
      `}</style>
      )}
      <div
        className="chat"
        style={{
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          ...(isFullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                height: '100vh',
                borderRadius: 0,
                boxShadow: 'none',
              }
            : {
                height: `calc(100vh - ${offsetHeight}px)`,
                borderRadius: token.borderRadius,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              }),
        }}
      >
        <Card
          className="mb-0"
          style={{ height: '100%' }}
          styles={{ body: { display: 'flex', padding: 0, height: '100%' } }}
        >
          {isMobile ? (
            <>
              {/* Mobile: Drawer for ChatMenu */}
              <Drawer
                placement="left"
                closable={false}
                forceRender
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                styles={{ body: { paddingLeft: 0, paddingRight: 0 }, wrapper: { width: 320 } }}
              >
                <div className="h-100">
                  <ChatMenu basePath={modulePath} onConversationsLoaded={setConversationCount} />
                </div>
              </Drawer>

              {/* Mobile main content */}
              <div
                style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div className="font-size-lg pt-3 px-3" style={{ flexShrink: 0 }}>
                  <MenuOutlined
                    onClick={() => setDrawerVisible(true)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                {conversationContent}
              </div>
            </>
          ) : (
            /* Desktop: Three-column splitter with draggable dividers */
            <Splitter style={{ height: '100%' }} onResize={handleMainSplitterResize} onCollapse={handleSplitterCollapse}>
              {/* Left Panel: Conversation List */}
              <Splitter.Panel
                defaultSize={storedSidebarWidth || DEFAULT_SIDEBAR_WIDTH}
                min={MIN_SIDEBAR_WIDTH}
                max={MAX_SIDEBAR_WIDTH}
                style={{ overflowX: 'hidden' }}
              >
                <ChatMenu basePath={modulePath} onConversationsLoaded={setConversationCount} />
              </Splitter.Panel>

              {/* Middle Panel: Conversation Content */}
              <Splitter.Panel style={{ minWidth: 400 }}>
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {conversationContent}
                </div>
              </Splitter.Panel>

              {/* Right Panel: Customer Intelligence */}
              {SHOW_INTELLIGENCE_PANEL && (
                <Splitter.Panel
                  defaultSize={intelligenceVisible ? Math.max(storedIntelligencePanelWidth || DEFAULT_INTELLIGENCE_PANEL_WIDTH, MIN_INTELLIGENCE_PANEL_WIDTH) : 0}
                  min={MIN_INTELLIGENCE_PANEL_WIDTH}
                  max={MAX_INTELLIGENCE_PANEL_WIDTH}
                  collapsible
                  style={{ overflowX: 'hidden' }}
                >
                  <CustomerIntelligencePanel
                    conversationId={params._id}
                    customerPhone={null} // Will be populated by conversation data
                    conversationData={null} // Will be populated by conversation data
                    isPanelVisible={!!params._id}
                  />
                </Splitter.Panel>
              )}
            </Splitter>
          )}
        </Card>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default OmnichannelInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format a source phone number for display.
 * Mirrors the same helper in ConversationHeader.jsx.
 */
function formatSourcePhone(phone) {
  if (!phone) return null;
  const str = String(phone).trim();
  const withPlus = str.startsWith('+') ? str : `+${str}`;
  const formatted = parsePhoneToDisplay(withPlus, undefined, { showCountryCode: true });
  if (formatted && formatted !== withPlus) return formatted;
  return withPlus;
}
